import {makeObservable, observable} from "mobx";
import {BufferAttribute, BufferGeometry} from "three";
import type {SpaceItem} from "../../../elements3d/SpaceItem";
import type {SpaceViewRenderer} from "../../../renderers/SpaceViewRenderer";
import type {Xyicon3D} from "../../../elements3d/Xyicon3D";
import type {BoundarySpaceMap3D} from "../../../elements3d/BoundarySpaceMap3D";
import type {Markup3D} from "../../../elements3d/markups/abstract/Markup3D";
import {Debouncer} from "../../../../../../../../utils/function/Debouncer";
import {InstancedIconManager} from "./InstancedIconManager";

// https://discourse.threejs.org/t/roundedrectangle-squircle/28645
const RectangleRounded = (w: number, h: number, r: number, s: number) => {
	const pi2 = Math.PI * 2;
	const n = (s + 1) * 4; // number of segments
	let indices: number[] = [];
	let positions: number[] = [];
	let uvs: number[] = [];
	let qu: number, sgx: number, sgy: number, x: number, y: number;

	const contour = (j: number) => {
		qu = Math.trunc((4 * j) / n) + 1; // quadrant	qu: 1..4
		sgx = qu === 1 || qu === 4 ? 1 : -1; // signum left/right
		sgy = qu < 3 ? 1 : -1; // signum top / bottom
		x = sgx * (w / 2 - r) + r * Math.cos((pi2 * (j - qu + 1)) / (n - 4)); // corner center + circle
		y = sgy * (h / 2 - r) + r * Math.sin((pi2 * (j - qu + 1)) / (n - 4));

		positions.push(x, y, 0);
		uvs.push(0.5 + x / w, 0.5 + y / h);
	};

	for (let j = 1; j < n; j++) {
		indices.push(0, j, j + 1); // 0 is center
	}

	indices.push(0, n, 1);
	positions.push(0, 0, 0); // rectangle center
	uvs.push(0.5, 0.5);

	for (let j = 0; j < n; j++) {
		contour(j);
	}

	const geometry = new BufferGeometry();
	geometry.setIndex(new BufferAttribute(new Uint32Array(indices), 1));
	geometry.setAttribute("position", new BufferAttribute(new Float32Array(positions), 3));
	geometry.setAttribute("uv", new BufferAttribute(new Float32Array(uvs), 2));

	return geometry;
};

export class LockIconManager extends InstancedIconManager {
	private _itemsWithLock: SpaceItem[] = [];
	protected override _defaultBackgroundColor: [number, number, number] = [54 / 255, 54 / 255, 54 / 255];
	protected override _iconColor: [number, number, number] = [1, 1, 1];
	private _selectedLockInstanceIds: Set<number> = new Set<number>();
	@observable
	private _hoveredLockInstanceId: number = -1;
	private readonly _updateDebouncer = new Debouncer(10);

	constructor(spaceViewRenderer: SpaceViewRenderer) {
		super(spaceViewRenderer, "lock");
		makeObservable(this);
		this._geometry = RectangleRounded(1, 1, 0.1, 5);
	}

	public updateTransformations() {
		if (this._icons) {
			this.updateTransformationsFromDummyObjects(
				this._icons.userData.selectedItems.map((item3D: Xyicon3D | BoundarySpaceMap3D | Markup3D) => item3D.getLockIconObject),
			);
		}
	}

	protected override prepareInstancedMesh() {
		this._itemsWithLock = this.getItemsWithLock();

		return {
			material: this._spaceViewRenderer.textureManager.miscTextures.lock,
			objects: this._itemsWithLock,
		};
	}

	protected onInstancedMeshCreated() {
		this._icons.userData.selectedItems = this._itemsWithLock;

		for (let i = 0; i < this._itemsWithLock.length; ++i) {
			const item3D = this._itemsWithLock[i];

			if (item3D.isHoveredForLock || item3D.isSelected) {
				item3D.lockInstanceId = i;
			}
		}
	}

	public override update = () => {
		this._updateDebouncer.debounce(() => super.update());
	};

	public mouseOver(lockInstanceId: number) {
		if (!this._selectedLockInstanceIds.has(lockInstanceId)) {
			this._hoveredLockInstanceId = lockInstanceId;
		}
	}

	public mouseOut(lockInstanceId: number) {
		if (!this._selectedLockInstanceIds.has(lockInstanceId)) {
			this._hoveredLockInstanceId = -1;
		}
	}

	public override removeIcons() {
		super.removeIcons();
		this._itemsWithLock.length = 0;
	}

	private getItemsWithLock() {
		const xyicons = this._spaceViewRenderer.xyiconManager.items.array.filter((xyicon) => xyicon.isPositionLockedAnyWay);
		const boundaries = this._spaceViewRenderer.boundaryManager.items.array.filter((boundary) => boundary.isPositionLockedAnyWay);
		const markups = this._spaceViewRenderer.markupManager.items.array.filter((markup) => markup.isPositionLockedAnyWay);
		return [...xyicons, ...boundaries, ...markups].filter((i) => i.isHoveredForLock || i.isSelected);
	}

	public getHoveredItem() {
		const items = this.getItemsWithLock();
		return items.find((i) => i.lockInstanceId === this.hoveredLockInstanceId) || null;
	}

	public get selectedLockInstanceIds() {
		return this._selectedLockInstanceIds;
	}

	public get hoveredLockInstanceId() {
		return this._hoveredLockInstanceId;
	}
}
